import { IPetDetailsForm } from "@/app/components/routes/auth_routes/OrderManagement/interface";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { UseFormReturn } from "react-hook-form";

interface IOptionItem {
  id: string;
  title: string;
}

interface IPetDetailsSectionProps {
  formHook: UseFormReturn<IPetDetailsForm>;
  isAddingNewPet: boolean;
  sectionOuterClass: string;
  sectionInnerClass: string;
  sectionTitleClass: string;
  fieldLabelClass: string;
  showAddPetRequired: boolean;
  petNameValue: string;
  petGenderValue: string;
  petSpeciesValue: string;
  petDobValue: string;
  petTemperatureValue: string;
  petWeightValue: string;
  petSelectItems: IOptionItem[];
  genderItems: IOptionItem[];
  speciesOptions: IOptionItem[];
  getCurrentDate: () => string;
  convertToValidDateStringFormat: (date: Date | string) => string;
  onQueueAutoSave: (section: "pet_details") => void;
  onHandlePetSelection: (petName: string) => void;
  onHandleAddPetClick: () => void;
}

const PetDetailsSection = ({
  formHook,
  isAddingNewPet,
  sectionOuterClass,
  sectionInnerClass,
  sectionTitleClass,
  fieldLabelClass,
  showAddPetRequired,
  petNameValue,
  petGenderValue,
  petSpeciesValue,
  petDobValue,
  petTemperatureValue,
  petWeightValue,
  petSelectItems,
  genderItems,
  speciesOptions,
  getCurrentDate,
  convertToValidDateStringFormat,
  onQueueAutoSave,
  onHandlePetSelection,
  onHandleAddPetClick,
}: IPetDetailsSectionProps) => {
  const uniformInputSizeClass = "w-[155px] h-9";
  const uniformSelectInputClass = `${uniformInputSizeClass} !py-0 !leading-[34px] text-left text-[#1F1F23]`;
  const uniformTextInputClass =
    `${uniformInputSizeClass} py-0 rounded-md border border-[#E2E2E2] bg-white px-3 text-xs text-[#1F1F23] ` +
    "placeholder:text-gray-400 focus:border-[#E2E2E2] focus:outline-none focus:ring-1 focus:ring-[#E2E2E2]";
  const normalizedPetName = String(petNameValue || "").trim();
  const selectedPetNameOption =
    petSelectItems.find((item) => item.title === normalizedPetName) ||
    (normalizedPetName ? { id: normalizedPetName, title: normalizedPetName } : undefined);

  return (
    <div className={sectionOuterClass} data-autosave-section="pet_details">
      <p className={sectionTitleClass}>Pet Details</p>
      <div className={`${sectionInnerClass} mt-2`}>
        <div className="grid w-full grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <div className="flex flex-col">
            <label className={`${fieldLabelClass} text-left`}>
              Pet Name <span className="text-[#ED0101]">*</span>
            </label>
            <SearchableSelectMenu
              onSelectItem={(item) => {
                if (item?.title) {
                  onHandlePetSelection(item.title);
                }
              }}
              onInputChange={(value) => {
                formHook.setValue("pet_name", value, { shouldValidate: true });
              }}
              onMenuInputBlur={(value) => {
                formHook.setValue("pet_name", value, { shouldValidate: true });
              }}
              allowCustomInput
              errorMessages={[]}
              fieldError={formHook.formState.errors.pet_name}
              register={formHook.register("pet_name", { required: true })}
              selectItems={petSelectItems}
                placeholder="Select or Add Pet Name"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              showAddActionQuery={false}
              defaultSelected={selectedPetNameOption}
              addAction={
                isAddingNewPet
                  ? undefined
                  : {
                      title: (
                        <span className="flex w-full items-center justify-center gap-2 py-1 text-xs">
                          <span className="text-xs font-medium leading-none text-[#2563EB]">+</span>
                          <span className="text-xs font-semibold text-[#111827]">Add Pet</span>
                        </span>
                      ),
                      callBack: () => onHandleAddPetClick(),
                    }
              }
              listBoxClassName="w-full"
              className={uniformSelectInputClass}
              inputContainerClassName={`${uniformInputSizeClass} flex items-center !py-0`}
              containerClassName={`${uniformInputSizeClass} [&_svg]:!h-4 [&_svg]:!w-4`}
            />
            {showAddPetRequired && !String(petNameValue || "").trim() && (
              <p className="mt-1 text-[10px] text-[#ED0101]">Required</p>
            )}
          </div>

          <div className="flex flex-col">
            <label className={`${fieldLabelClass} text-left`}>
              Pet Gender <span className="text-[#ED0101]">*</span>
            </label>
            <SearchableSelectMenu
              onSelectItem={(item) => {
                if (item?.title) {
                  formHook.setValue("pet_gender", item.title);
                  onQueueAutoSave("pet_details");
                }
              }}
              errorMessages={[]}
              fieldError={formHook.formState.errors.pet_gender}
              register={formHook.register("pet_gender", { required: true })}
              selectItems={genderItems}
              placeholder="Select Gender"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={
                genderItems.filter((gi) => {
                  const title = gi?.title ?? "";
                  const id = gi?.id ?? "";
                  const currentGender = formHook.watch("pet_gender")?.toLowerCase() ?? "";
                  return title?.toLowerCase() === currentGender || id?.toLowerCase() === currentGender;
                })[0]
              }
              listBoxClassName="w-full"
              className={uniformSelectInputClass}
              inputContainerClassName={`${uniformInputSizeClass} flex items-center !py-0`}
              containerClassName={`${uniformInputSizeClass} [&_svg]:!h-4 [&_svg]:!w-4`}
            />
            {showAddPetRequired && !String(petGenderValue || "").trim() && (
              <p className="mt-1 text-[10px] text-[#ED0101]">Required</p>
            )}
          </div>

          <div className="flex flex-col">
            <label className={`${fieldLabelClass} text-left`}>
              Species <span className="text-[#ED0101]">*</span>
            </label>
            <SearchableSelectMenu
              onSelectItem={(item) => {
                if (item?.id) {
                  formHook.setValue("pet_specie", item.id);
                  onQueueAutoSave("pet_details");
                }
              }}
              errorMessages={[]}
              fieldError={formHook.formState.errors.pet_specie}
              register={formHook.register("pet_specie", { required: true })}
              selectItems={speciesOptions}
              placeholder="Select Species"
              showTooltips={false}
              showTypedErrors={false}
              showDropdownIcon
              defaultSelected={speciesOptions.find((item) => item.id === formHook.watch("pet_specie"))}
              listBoxClassName="w-full"
              className={uniformSelectInputClass}
              inputContainerClassName={`${uniformInputSizeClass} flex items-center !py-0`}
              containerClassName={`${uniformInputSizeClass} [&_svg]:!h-4 [&_svg]:!w-4`}
            />
            {showAddPetRequired && !String(petSpeciesValue || "").trim() && (
              <p className="mt-1 text-[10px] text-[#ED0101]">Required</p>
            )}
          </div>

          <div className="flex flex-col">
            <label className={`${fieldLabelClass} text-left`}>
              DOB <span className="text-[#ED0101]">*</span>
            </label>
            <div className="[&_svg]:!h-4 [&_svg]:!w-4">
              <PWLCalendarComponent
                showLeftNav={false}
                allowPastDaySelection={true}
                errorMessages={[]}
                register={formHook.register("pet_dob", { required: true })}
                showTypedErrors={false}
                disableDatesAfterDate={getCurrentDate()}
                defaultSelectedDate={formHook.watch("pet_dob")}
                disabledFutureDateYearMonthSelection
                fieldError={formHook.formState.errors.pet_dob}
                onSelectDate={(date) => {
                  const dobString = convertToValidDateStringFormat(date);
                  formHook.setValue("pet_dob", dobString);
                  onQueueAutoSave("pet_details");
                }}
                className={`${uniformInputSizeClass} !py-0 !leading-[34px]`}
              />
            </div>
            {showAddPetRequired && !String(petDobValue || "").trim() && (
              <p className="mt-1 text-[10px] text-[#ED0101]">Required</p>
            )}
          </div>

          <div className="flex flex-col">
            <label className={`${fieldLabelClass} text-left`}>
              Temperature
            </label>
            <div className={`relative ${uniformInputSizeClass}`}>
              <input
                type="number"
                min={85}
                max={115}
                placeholder="Temperature"
                {...formHook.register("pet_tempeture", {
                  validate: (value) => {
                    const normalized = String(value ?? "").trim();
                    if (!normalized) return true;
                    const temp = Number(normalized);
                    if (Number.isNaN(temp)) return "Invalid temperature range";
                    return (temp >= 85 && temp <= 115) || "Invalid temperature range";
                  },
                })}
                className={`${uniformTextInputClass} pr-8`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-600">&deg;F</span>
            </div>
            {formHook.formState.errors.pet_tempeture?.message && (
              <p className="mt-1 text-[10px] text-[#ED0101]">
                {String(formHook.formState.errors.pet_tempeture.message)}
              </p>
            )}
          </div>

          <div className="flex flex-col">
            <label className={`${fieldLabelClass} text-left`}>
              Weight <span className="text-[#ED0101]">*</span>
            </label>
            <div className={`relative ${uniformInputSizeClass}`}>
              <input
                type="text"
                placeholder="Weight"
                {...formHook.register("pet_weight", { required: true })}
                className={`${uniformTextInputClass} pr-8`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-600">Kg</span>
            </div>
            {showAddPetRequired && !String(petWeightValue || "").trim() && (
              <p className="mt-1 text-[10px] text-[#ED0101]">Required</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetDetailsSection;
